/**
 * What Roger says about a clinician nobody has signed in as.
 *
 * Several surfaces show these names. The treatment is the same everywhere;
 * the explanation is not, so the wording lives here once and each surface
 * names the moment it is in.
 *
 * - `assignment` — a name where this surface offers no way to change it, and
 *   nothing more is known about why: the episode header. Two lines, no ask.
 * - `reassignable` — a task row whose picker is right there. The two lines
 *   explain; the row supplies the act (a Reassign button) as the third.
 * - `locked` — a task row that cannot change hands. The two lines explain;
 *   the row appends the reason it is locked, so the popover never offers a
 *   reassignment the row would refuse.
 * - `directory` — the User Manager, where the reader administers the row
 *   itself and wants to know why it is here and what clears it.
 * - `carry` — the reader is handing an imported chart's existing assignment
 *   forward to a new episode, which Roger allows, so the copy
 *   offers the choice rather than refusing it.
 */
export type UnclaimedClinicianVariant =
  'assignment' | 'reassignable' | 'locked' | 'directory' | 'carry';

const IMPORTED_LINES = [
  'This clinician was imported from External EMR.',
  'They have no Roger account for this clinic yet.',
];

const COPY: Record<
  UnclaimedClinicianVariant,
  { label: (name: string) => string; lines: string[] }
> = {
  assignment: {
    label: name => `Why ${name} cannot be assigned new work`,
    lines: IMPORTED_LINES,
  },
  reassignable: {
    label: name => `Why ${name} is marked imported`,
    lines: IMPORTED_LINES,
  },
  locked: {
    label: name => `Why ${name} is marked imported`,
    lines: IMPORTED_LINES,
  },
  carry: {
    label: name => `Why ${name} is marked imported`,
    lines: [
      'This person was imported from External EMR.',
      'They have no Roger account for this clinic yet.',
      'Keep them, or choose someone with an account.',
    ],
  },
  directory: {
    label: name => `Why ${name} is not in assignment lists`,
    lines: [
      'This member was imported from External EMR.',
      'Nobody has signed in to the account yet.',
      'Roger keeps them out of assignment lists until someone does.',
    ],
  },
};

/**
 * The popover label and lines for one variant.
 *
 * `UnclaimedClinicianName` marks the name. A surface that marks something
 * else — Add Episode marks the *field*, because a Radix select trigger is a
 * button and the marker's own button cannot sit inside it — reads the copy
 * from here so there is still only one copy.
 *
 * `detailLines` follow the variant's own lines. A `locked` row says why it is
 * locked there; a `reassignable` row on a closed episode says the edit will
 * be logged as an amendment.
 */
export function unclaimedClinicianCopy(
  variant: UnclaimedClinicianVariant,
  name: string,
  detailLines: string[] = []
): { popoverLabel: string; lines: string[] } {
  const copy = COPY[variant];
  return {
    popoverLabel: copy.label(name),
    lines: [...copy.lines, ...detailLines],
  };
}
